// AdminSubscriptions — admin sub-view, extracted verbatim from App.js.
import { useState, useEffect } from "react";
import { C } from "../lib/theme";
import { ConfirmDialog } from "../components/ui";
import { getFirstSiteRate, getAdditionalRate, getCurrencySymbol } from "../lib/pricing";
import { createWorkspaceInDB, renameWorkspaceInDB } from "../data/db";
import { getCurrentSession } from "../data/auth";
import { API_BASE } from "../lib/api";
import { useT } from "../lib/i18n";
import { isOverrideActive, isOverrideCapped } from "../lib/accountAccess";

// (The pre-trial CardRequiredModal that used to gate the first workspace
// behind Stripe Checkout was removed with the no-card trial — migration 041.
// Adding a card / paying now lives in App.js UpgradeModal.)

// Online-retail accounts (accounts.business_type = 'retail') sell on online
// marketplaces, not food-delivery aggregators. Same set for every market for
// now; mirrors RETAIL_PLATFORMS in backend/stripe_routes.js and the retail
// channel presets in backend/admin_routes.js.
const RETAIL_PLATFORMS = {
  "Debenhams":   {color:"#111111", commission:0.25, note:"25% commission"},
  "Amazon":      {color:"#FF9900", commission:0.15, note:"15% referral fee"},
  "eBay":        {color:"#E53238", commission:0.13, note:"13% final value fee"},
  "TikTok Shop": {color:"#FE2C55", commission:0.09, note:"9% commission"},
};

export function AdminSubscriptions({workspaces, setWorkspaces, showAddOnly=false, account, user, refreshAccount, onShowUpgrade, onCreated}) {
  const { t } = useT();
  const isSuperAdmin = user?.plan === "superadmin";
  // Account currency dictates the workspace market for non-super-admins
  const accountCurrency = account?.billing_currency || "GBP";
  const defaultMarket = accountCurrency === "AED" ? "uae" : accountCurrency === "USD" ? "us" : accountCurrency === "INR" ? "in" : "uk";
  // Retail accounts get the marketplace platform set in place of the
  // market's food-delivery aggregators throughout the wizard.
  const isRetail = account?.business_type === "retail";

  // Detect in-flight Stripe redirect — if there's a stashed pending workspace,
  // the post-stripe handler in App is about to create it. Don't auto-open the
  // workspace creator, otherwise the user sees it as an "empty" state and
  // fills the form again, creating a duplicate workspace.
  const hasPendingStash = (() => {
    try { return !!localStorage.getItem("netrev_pending_workspace"); }
    catch(e) { return false; }
  })();

  const [adding, setAdding] = useState(
    showAddOnly || (Object.keys(workspaces).length === 0 && !hasPendingStash)
  );
  const [brand,  setBrand]  = useState("");
  const [city,   setCity]   = useState("");
  const [area,   setArea]   = useState("");
  const [market, setMarket] = useState(defaultMarket);
  const [platforms, setPlatforms] = useState(isRetail
    ? Object.keys(RETAIL_PLATFORMS)
    : defaultMarket === "uae"
    ? ["Talabat","Noon Food","Careem Food","Deliveroo","Keeta"]
    : defaultMarket === "us"
    ? ["Uber Eats","DoorDash","Grubhub"]
    : defaultMarket === "in"
    ? ["Zomato","Swiggy","magicpin","ONDC","EatSure","EatClub","toing","Blinkit","Thrive","DotPe"]
    : ["Deliveroo","Uber Eats","JustEat"]);
  const [step,   setStep]   = useState(1);
  const [confirm, setConfirm] = useState(null);  // Styled confirm dialog state
  const [editingWsId, setEditingWsId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [renameErr, setRenameErr] = useState("");
  const wsList = Object.values(workspaces);

  // Safety net: if the workspace creator is open but a workspace just appeared
  // (e.g. post-Stripe auto-creation completed), close the creator so user doesn't
  // accidentally create a duplicate by re-filling the form. Also clear form state.
  useEffect(() => {
    if (adding && !showAddOnly && wsList.length > 0 && !hasPendingStash) {
      // Only auto-close if user wasn't manually opening it via the "+ Add workspace" button.
      // We detect "manually opening" by checking that there's no in-flight stash. If wsList just became non-empty, the form opened
      // because of empty state, not user intent — close it.
      const stashStillThere = (() => {
        try { return !!localStorage.getItem("netrev_pending_workspace"); }
        catch(e) { return false; }
      })();
      if (!stashStillThere) {
        setAdding(false);
        setBrand(""); setCity(""); setArea(""); setStep(1);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsList.length]);

  const MARKETS = {
    uk: {
      label:"🇬🇧 United Kingdom",
      currency:"GBP", sym:"£",
      cities:["London","Manchester","Birmingham","Leeds","Sheffield","Rotherham","Leicester","Bristol","Liverpool","Glasgow"],
      platforms:{
        "Deliveroo":    {color:"#00CCBC", commission:0.35, note:"35% commission"},
        "Uber Eats":    {color:"#06C167", commission:0.30, note:"30% commission"},
        "JustEat":      {color:"#FF8000", commission:0.33, note:"33% commission"},
      }
    },
    uae: {
      label:"🇦🇪 United Arab Emirates",
      currency:"AED", sym:"AED ",
      cities:["Dubai","Abu Dhabi","Sharjah","Ajman","Ras Al Khaimah","Fujairah","Al Ain"],
      platforms:{
        "Talabat":      {color:"#FF6B00", commission:0.33, note:"33% commission — largest in UAE"},
        "Noon Food":    {color:"#FEEE00", textColor:"#333", commission:0.28, note:"28% commission"},
        "Careem Food":  {color:"#1DBF73", commission:0.30, note:"30% commission"},
        "Deliveroo":    {color:"#00CCBC", commission:0.30, note:"30% commission"},
        "Keeta":        {color:"#FFD500", textColor:"#333", commission:0.25, note:"25% commission — Meituan-owned"},
      }
    },
    us: {
      label:"🇺🇸 United States",
      currency:"USD", sym:"$",
      cities:["New York","Los Angeles","Chicago","Houston","Miami","Dallas","Atlanta","Philadelphia","Phoenix","San Francisco"],
      platforms:{
        "Uber Eats":    {color:"#06C167", commission:0.30, note:"30% commission"},
        "DoorDash":     {color:"#FF3008", commission:0.30, note:"30% commission — largest in US"},
        "Grubhub":      {color:"#F63440", commission:0.30, note:"30% commission"},
      }
    },
    in: {
      label:"🇮🇳 India",
      currency:"INR", sym:"₹",
      cities:["Mumbai","Delhi","Bengaluru","Hyderabad","Chennai","Pune","Kolkata","Ahmedabad","Jaipur","Gurugram"],
      platforms:{
        "Zomato":       {color:"#E23744", commission:0.25, note:"25% commission — duopoly leader"},
        "Swiggy":       {color:"#FC8019", commission:0.25, note:"25% commission"},
        "magicpin":     {color:"#EC008C", commission:0.15, note:"15% commission — largest ONDC seller app"},
        "ONDC":         {color:"#1B4A8B", commission:0.08, note:"~8% network fees — open network"},
        "EatSure":      {color:"#FFB300", textColor:"#333", commission:0.20, note:"20% commission — Rebel Foods marketplace"},
        "EatClub":      {color:"#111111", commission:0.20, note:"20% commission — BOX8/MOJO brands"},
        "toing":        {color:"#0FA84F", commission:0.10, note:"~10% commission"},
        "Blinkit":      {color:"#F8CB46", textColor:"#333", commission:0.15, note:"15% — quick-commerce (grocery)"},
        "Thrive":       {color:"#00A676", commission:0.03, note:"~3% — direct ordering"},
        "DotPe":        {color:"#4C3FE1", commission:0.03, note:"~3% — direct ordering"},
      }
    }
  };

  const currentMarket = MARKETS[market];
  // The platform set the wizard actually offers — marketplace set for retail
  // accounts, the market's aggregators for everyone else.
  const currentPlatforms = isRetail ? RETAIL_PLATFORMS : currentMarket.platforms;

  function switchMarket(m){ setMarket(m); setPlatforms(Object.keys(isRetail ? RETAIL_PLATFORMS : MARKETS[m].platforms)); }

  // The account row loads async — if it arrives after mount and flips the
  // retail flag, replace the default platform selection with the right set.
  useEffect(() => {
    setPlatforms(Object.keys(currentPlatforms));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRetail]);

  function startRename(ws){ setEditingWsId(ws.id); setEditingName(ws.name); setRenameErr(""); }
  function cancelRename(){ setEditingWsId(null); setEditingName(""); setRenameErr(""); }
  async function saveRename(wsId){
    const newName = editingName.trim();
    if(!newName){ setRenameErr(t("subs.err.nameEmpty")); return; }
    const { error } = await renameWorkspaceInDB(wsId, newName);
    if(error){ setRenameErr(error.message || t("subs.err.couldntSaveName")); return; }
    setWorkspaces(prev=>({...prev,[wsId]:{...prev[wsId],name:newName}}));
    cancelRename();
  }

  const [saving, setSaving] = useState(false);
  // In-flight guard for the archived-list "Complete payment" button (that path
  // skips ConfirmDialog's busy flag — a double-click must not fire two
  // concurrent restore/charge attempts).
  const [restoringWsId, setRestoringWsId] = useState(null);

  async function createWorkspace(){
    if(!brand||!city) return;
    setSaving(true);

    // Trial users selected a single platform via the radio picker (platforms state contains 1 item).
    // Paid users have platforms state = all platforms (default from useState initializer).
    // free_override users (while unexpired) bypass the PLATFORM restriction —
    // full features — but NOT the one-workspace limit below.
    const isTrialOrPreTrial = !account || account.subscription_status === "trialing";
    const isFreeOverride = isOverrideActive(account);
    const trialChannelLimit = isTrialOrPreTrial && !isFreeOverride;

    // ONE-workspace limit (matches the RESTRICTIVE RLS policy, migration 029):
    // trials AND free-override accounts are capped at a single active
    // workspace; only an own paid subscription lifts the cap. Without this
    // guard RLS would silently reject the insert and the user sees nothing.
    const activeWsCount = wsList.filter(w => !w.archived_at).length;
    const cappedAtOneWorkspace = trialChannelLimit || isOverrideCapped(account);
    if (cappedAtOneWorkspace && activeWsCount >= 1) {
      setSaving(false);
      setAdding(false);
      alert(isFreeOverride ? t("subs.alert.freeOverrideOneWorkspace") : t("subs.alert.trialOneWorkspace"));
      if (onShowUpgrade) onShowUpgrade();
      return;
    }
    // For trial, ensure exactly 1 platform is used; for paid, use whatever the user has selected
    // (defaults to all market platforms via useState init).
    let platformList;
    if (trialChannelLimit) {
      // Defensive: if for some reason platforms state has multiple, take just the first.
      // Fall back to the market's first platform if state is empty.
      const firstSelected = platforms[0] || Object.keys(currentPlatforms)[0];
      platformList = [firstSelected];
    } else {
      platformList = platforms.length > 0 ? platforms : Object.keys(currentPlatforms);
    }

    // Start every channel from the market preset commission (the % shown in
    // the picker) instead of 0 — a fresh workspace should show a realistic
    // margin on its first log, not 100%. Editable later in Configuration.
    const channelsData = platformList.map(p => ({
      platform: p,
      color: currentPlatforms[p]?.color || "#1a73e8",
      commission: currentPlatforms[p]?.commission ?? 0,
      cardFeePct: 0,
    }));
    const firstPlatform = currentPlatforms[platformList[0]];

    // NO-CARD TRIAL (migration 041): the first workspace is created directly —
    // the database starts the 14-day trial on insert. No Stripe round-trip, no
    // card wall. Adding a card (prompted from day 10) or subscribing after the
    // trial goes through the Upgrade modal / TrialEndPaywall instead.

    // Save to Supabase
    const session = await getCurrentSession();
    const userId = session?.user?.id;

    if(userId){
      const result = await createWorkspaceInDB(userId, {
        name:brand, city, area, market,
        currency:currentMarket.currency,
        color:firstPlatform?.color||"#1a73e8",
      }, channelsData);

      if(result){
        // Build local state from DB result
        const newChannels = {};
        result.channels.forEach(ch => {
          newChannels[ch.id] = {
            id:ch.id, platform:ch.platform,
            color:ch.color, commission:ch.commission,
            history:[], menu:[],
          };
        });
        setWorkspaces(prev=>({...prev,[result.wsId]:{
          id:result.wsId, name:brand, city, area,
          market, currency:currentMarket.currency,
          color:firstPlatform?.color||"#1a73e8",
          platform:platformList.join(" + "),
          status:"trial", history:[], menu:[],
          fixedCosts:{rent:0,wages:0,utilities:0,other:0},
          channels:newChannels,
        }}));
        setBrand("");setCity("");setArea("");
        setAdding(false);setStep(1);
        if (refreshAccount) {
          // The trigger just stamped trial_ends_at — pull it so the trial
          // banner / status chip update without a reload.
          setTimeout(() => refreshAccount(), 500);
        }
        // First workspace ever → hand off to the first-log flow (Daily Log
        // opens with the add form ready) so the user sees a real margin
        // within a minute of signing up.
        if (onCreated && Object.keys(workspaces || {}).length === 0) {
          try { onCreated(result.wsId, platformList[0]); } catch (e) { console.warn("onCreated:", e); }
        }
        // Return the workspace id so callers (e.g. doCreate) can roll back
        // by archiving it if a subsequent Stripe charge fails.
        return result.wsId;
      } else {
        alert(t("subs.alert.saveFailed"));
        return null;
      }
    } else {
      alert(t("subs.alert.sessionExpired"));
    }
    setSaving(false);
  }

  return (
    <div className="nv-page" style={{padding:"28px 24px 40px",maxWidth:880,margin:"0 auto"}}>
      {/* Header — navy brand accent */}
      <div style={{marginBottom:28,display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:20,flexWrap:"wrap"}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:11,marginBottom:6}}>
            <div style={{width:32,height:32,borderRadius:8,background:"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 6px rgba(15,23,42,.20)"}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 4h20v16H2z"/><path d="M2 10h20"/><path d="M6 15h2"/><path d="M11 15h2"/>
              </svg>
            </div>
            <h1 style={{fontSize:22,fontWeight:700,color:"#0f172a",letterSpacing:"-.5px",margin:0,lineHeight:1.2}}>{t("subs.header.title")}</h1>
          </div>
          <p style={{fontSize:13,color:"#64748b",margin:0,fontWeight:400,lineHeight:1.5,maxWidth:560,paddingLeft:43}}>{t("subs.header.desc")}</p>
        </div>
        {wsList.length > 0 && !adding && (() => {
          // ONE-workspace limit: trial AND free-override accounts get 1 max.
          // Show upgrade prompt instead of opening the creator when at limit.
          const activeCount = wsList.filter(w => !w.archived_at).length;
          const isTrialing = account?.subscription_status === "trialing";
          const atTrialLimit = (isTrialing || isOverrideCapped(account)) && activeCount >= 1;

          if (atTrialLimit) {
            return (
              <button
                onClick={() => onShowUpgrade && onShowUpgrade()}
                title={t("subs.upgrade.title")}
                style={{
                  padding:"9px 16px",
                  borderRadius:8,
                  border:"1px solid #e2e8f0",
                  background:"#fff",
                  color:"#0f172a",
                  fontSize:13,
                  fontWeight:600,
                  cursor:"pointer",
                  fontFamily:"inherit",
                  letterSpacing:"-.1px",
                  display:"inline-flex",
                  alignItems:"center",
                  gap:8,
                  boxShadow:"0 1px 2px rgba(15,23,42,.04)",
                }}
              >
                <span style={{fontSize:10,fontWeight:700,letterSpacing:.5,textTransform:"uppercase",color:"#475569",background:"#f1f5f9",padding:"3px 7px",borderRadius:4}}>{t("subs.badge.trial")}</span>
                {t("subs.upgrade.cta")}
              </button>
            );
          }
          return (
            <button onClick={()=>setAdding(true)} style={{padding:"9px 16px",borderRadius:8,border:"none",background:"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",letterSpacing:"-.1px",boxShadow:"0 2px 6px rgba(15,23,42,.18)"}}>{t("subs.addWorkspace")}</button>
          );
        })()}
      </div>

      {/* Billing summary — single card at top consolidates plan, renewal, action */}
      {wsList.length > 0 && account && (() => {
        const isPendingCancel = account?.cancel_at_period_end === true;
        const renewalDate = account.current_period_end
          ? new Date(account.current_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
          : null;
        const activeWorkspaces = wsList.filter(w => !w.archived_at);
        // BILLABLE = non-archived AND not pending cancel. This is what Stripe actually charges for.
        const billableWorkspaces = activeWorkspaces.filter(w => !w.cancel_at_period_end);
        const activeCount = activeWorkspaces.length;
        const billableCount = billableWorkspaces.length;
        // Compute total monthly cost — based on BILLABLE count, not total active.
        // Pending-cancel workspaces are accessible until period end but not billed for the next cycle.
        const totalCost = billableWorkspaces.reduce((sum, ws, idx) => {
          const wsCurrency = (ws.market === "uae" || ws.market === "UAE") ? "AED" : (ws.market === "us" || ws.market === "US") ? "USD" : (ws.market === "in" || ws.market === "IN") ? "INR" : "GBP";
          const isFirst = idx === 0;
          const rate = isFirst ? getFirstSiteRate(wsCurrency) : getAdditionalRate(wsCurrency);
          return sum + rate;
        }, 0);
        const symbol = getCurrencySymbol(billableWorkspaces[0]?.market === "uae" || billableWorkspaces[0]?.market === "UAE" ? "AED" : billableWorkspaces[0]?.market === "us" || billableWorkspaces[0]?.market === "US" ? "USD" : billableWorkspaces[0]?.market === "in" || billableWorkspaces[0]?.market === "IN" ? "INR" : "GBP");
        const planLabel = account.subscription_plan === "annual" ? t("subs.billing.plan.annual") : t("subs.billing.plan.monthly");

        return (
          <div style={{marginBottom:24,background:"#fff",border:"1px solid #e2e8f0",borderLeft:`3px solid ${isPendingCancel?"#d97706":"#1e40af"}`,borderRadius:10,overflow:"hidden",boxShadow:"0 1px 2px rgba(15,23,42,.04)"}}>
            <div style={{padding:"18px 22px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:18,flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:240}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:10.5,fontWeight:700,color:isPendingCancel?"#92400e":"#1e40af",textTransform:"uppercase",letterSpacing:1,background:isPendingCancel?"#fef3c7":"#dbeafe",padding:"3px 8px",borderRadius:4}}>
                    <span style={{width:5,height:5,borderRadius:3,background:isPendingCancel?"#d97706":"#1e40af"}}/>
                    {isPendingCancel ? t("subs.billing.pendingCancel") : t("subs.billing.currentPlan")}
                  </span>
                </div>
                <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                  <span style={{fontSize:26,fontWeight:700,color:"#0f172a",letterSpacing:"-.7px",fontVariantNumeric:"tabular-nums"}}>
                    {symbol}{totalCost.toFixed(totalCost < 1 ? 3 : 2)}
                  </span>
                  <span style={{fontSize:13,color:"#64748b",fontWeight:500}}>
                    / {account.subscription_plan === "annual" ? t("subs.billing.per.year") : t("subs.billing.per.month")}
                  </span>
                </div>
                <div style={{fontSize:13,color:"#475569",fontWeight:500,letterSpacing:"-.1px"}}>
                  {planLabel} · {billableCount} {billableCount===1?t("subs.billing.billableWorkspace"):t("subs.billing.billableWorkspaces")}
                  {activeCount > billableCount && (
                    <span style={{color:"#94a3b8",fontWeight:400}}>{t("subs.billing.pendingCancelParen.prefix")}{activeCount - billableCount}{t("subs.billing.pendingCancelParen.suffix")}</span>
                  )}
                  {renewalDate && (
                    <>
                      <span style={{color:"#cbd5e1",margin:"0 8px"}}>·</span>
                      <span style={{color: isPendingCancel ? "#b45309" : "#16a34a", fontWeight:600}}>
                        {isPendingCancel ? `${t("subs.billing.cancelsPrefix")}${renewalDate}` : `${t("subs.billing.renewsPrefix")}${renewalDate}`}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                {isPendingCancel && (
                  <button onClick={()=>{
                    setConfirm({
                      title: t("subs.confirm.keepSub.title"),
                      body: `${t("subs.confirm.keepSub.bodyPrefix")}${activeCount}${activeCount===1?t("subs.confirm.keepSub.bodyWorkspace"):t("subs.confirm.keepSub.bodyWorkspaces")}${t("subs.confirm.keepSub.bodySuffix")}`,
                      confirmLabel: t("subs.confirm.keepSub.confirmLabel"),
                      confirmVariant: "primary",
                      onConfirm: async () => {
                        const session = await getCurrentSession();
                        const token = session?.access_token;
                        const res = await fetch(`${API_BASE}/api/stripe/reactivate-subscription`, {
                          method: "POST",
                          headers: { "Authorization": `Bearer ${token}` },
                        });
                        const d = await res.json();
                        if (!res.ok) throw new Error(d.error || t("subs.err.failedReactivate"));
                        setWorkspaces(p => {
                          const next = {...p};
                          for (const id in next) {
                            if (!next[id].archived_at && next[id].cancel_at_period_end) {
                              next[id] = {...next[id], cancel_at_period_end: false, cancel_scheduled_for: null};
                            }
                          }
                          return next;
                        });
                      },
                    });
                  }} style={{padding:"8px 14px",borderRadius:7,border:"none",background:"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",color:"#fff",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",letterSpacing:"-.1px",boxShadow:"0 2px 5px rgba(15,23,42,.18)"}}>
                    {t("subs.billing.keepSubscription")}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Active workspaces — list rows with workspace colors and market pills */}
      {wsList.filter(w=>!w.archived_at).length > 0 && (
        <>
          <div style={{display:"flex",alignItems:"center",gap:10,margin:"4px 0 12px"}}>
            <h2 style={{fontSize:12.5,fontWeight:700,color:"#475569",margin:0,letterSpacing:"-.05px",textTransform:"uppercase",letterSpacing:0.6}}>{t("subs.section.workspaces")}</h2>
            <div style={{flex:1,height:1,background:"#e2e8f0"}}/>
          </div>
          <div style={{border:"1px solid #e2e8f0",borderRadius:10,overflow:"hidden",background:"#fff",marginBottom:24,boxShadow:"0 1px 2px rgba(15,23,42,.04)"}}>
            {wsList.filter(w=>!w.archived_at).map((ws, idx, arr)=>{
              const isCanceling = !!ws.cancel_at_period_end;
              const scheduledDate = ws.cancel_scheduled_for ? new Date(ws.cancel_scheduled_for).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;
              const wsCurrency = ws.market === "uae" || ws.market === "UAE" ? "AED" : ws.market === "us" || ws.market === "US" ? "USD" : ws.market === "in" || ws.market === "IN" ? "INR" : "GBP";
              const wsSymbol = getCurrencySymbol(wsCurrency);
              const allBillable = wsList.filter(w => !w.archived_at && !w.cancel_at_period_end);
              // The "first site" status applies to the first BILLABLE workspace, not the first non-archived.
              // A pending-cancel workspace doesn't claim the first-site tier — that goes to whatever's
              // actually being charged.
              const isFirst = allBillable[0]?.id === ws.id;
              const rate = ws.cancel_at_period_end
                ? 0  // pending-cancel workspaces aren't billed for the upcoming cycle
                : (isFirst ? getFirstSiteRate(wsCurrency) : getAdditionalRate(wsCurrency));
              const isLast = idx === arr.length - 1;
              const isUAE = ws.market === "uae" || ws.market === "UAE";
              const isUS  = ws.market === "us"  || ws.market === "US";
              const isIN  = ws.market === "in"  || ws.market === "IN";

              return (
                <div key={ws.id} className="nv-flex-wrap-sm" style={{padding:"14px 18px",borderBottom:isLast?"none":"1px solid #f1f5f9",display:"flex",alignItems:"center",gap:14,background:isCanceling?"#fffdf8":"#fff",transition:"background .12s"}}>
                  {/* Workspace color dot */}
                  <div style={{width:10,height:10,borderRadius:5,background:ws.color||"#1e40af",flexShrink:0,boxShadow:`0 0 0 3px ${(ws.color||"#1e40af")}1a`}}/>

                  {/* Name + meta */}
                  <div className="nv-ws-name" style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:3}}>
                      {editingWsId === ws.id ? (
                        <>
                          <input value={editingName} autoFocus
                            onChange={e=>{setEditingName(e.target.value);setRenameErr("");}}
                            onKeyDown={e=>{if(e.key==="Enter")saveRename(ws.id);else if(e.key==="Escape")cancelRename();}}
                            style={{width:`${Math.max(editingName.length+3,12)}ch`,maxWidth:260,padding:"3px 8px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13.5,fontWeight:600,color:"#0f172a",letterSpacing:"-.15px",background:"#fff",fontFamily:"inherit",outline:"none",boxSizing:"content-box"}}/>
                          <button onClick={()=>saveRename(ws.id)} style={{padding:"4px 10px",borderRadius:6,border:"none",background:"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",color:"#fff",fontSize:11.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",letterSpacing:"-.1px"}}>{t("subs.rename.save")}</button>
                          <button onClick={cancelRename} style={{padding:"4px 10px",borderRadius:6,border:"1px solid #e2e8f0",background:"#fff",color:"#64748b",fontSize:11.5,fontWeight:500,cursor:"pointer",fontFamily:"inherit",letterSpacing:"-.1px"}}>{t("subs.rename.cancel")}</button>
                          {renameErr && <span style={{fontSize:11.5,color:C.red,fontWeight:500}}>{renameErr}</span>}
                        </>
                      ) : (
                        <>
                          <span style={{fontSize:14,fontWeight:600,color:"#0f172a",letterSpacing:"-.15px"}}>{ws.name}</span>
                          <button onClick={()=>startRename(ws)} title={t("subs.rename.tooltip")} style={{padding:2,border:"none",background:"none",cursor:"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center",lineHeight:0}}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                          </button>
                        </>
                      )}
                      {(ws.city||ws.area) && <span style={{fontSize:13,color:"#64748b",fontWeight:400}}>{ws.city}{ws.area?`, ${ws.area}`:""}</span>}
                      <span style={{fontSize:10.5,padding:"2px 7px",borderRadius:4,background:isUAE?"#ecfdf5":isUS?"#fef2f2":isIN?"#fff7ed":"#eff6ff",color:isUAE?"#047857":isUS?"#b91c1c":isIN?"#c2410c":"#1e40af",fontWeight:700,letterSpacing:.4,border:`1px solid ${isUAE?"#a7f3d0":isUS?"#fecaca":isIN?"#fed7aa":"#bfdbfe"}`}}>
                        {isUAE?t("subs.pill.uae"):isUS?t("subs.pill.us"):isIN?t("subs.pill.in"):t("subs.pill.uk")}
                      </span>
                      {isCanceling && (
                        <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10.5,padding:"2px 7px 2px 6px",borderRadius:4,background:"#fef3c7",color:"#92400e",fontWeight:700,letterSpacing:.3,border:"1px solid #fde68a"}}>
                          <span style={{width:5,height:5,borderRadius:3,background:"#d97706"}}/>
                          {t("subs.cancelPrefix")}{scheduledDate?.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div style={{fontSize:12,color:"#94a3b8",fontWeight:400,marginTop:2,lineHeight:1.4}}>
                      {Object.values(ws.channels||{}).slice(0,4).map(c=>c.platform).join(" · ")}
                      {Object.values(ws.channels||{}).length > 4 && ` · +${Object.values(ws.channels||{}).length - 4}`}
                    </div>
                  </div>

                  {/* Price — pending-cancel workspaces show "no future charge" */}
                  <div style={{textAlign:"right",flexShrink:0,marginRight:4}}>
                    {ws.cancel_at_period_end ? (
                      <>
                        <div style={{fontSize:13,fontWeight:500,color:"#94a3b8",letterSpacing:"-.2px"}}>
                          {t("subs.price.noFutureCharges")}
                        </div>
                        <div style={{fontSize:10,color:"#94a3b8",fontWeight:500,marginTop:1,letterSpacing:.2}}>{t("subs.price.accessUntilCancel")}</div>
                      </>
                    ) : (
                      <>
                        <div style={{fontSize:14,fontWeight:600,color:"#0f172a",fontVariantNumeric:"tabular-nums",letterSpacing:"-.2px"}}>
                          {wsSymbol}{rate.toFixed(rate < 1 ? 3 : 2)}<span style={{fontSize:11,color:"#94a3b8",fontWeight:500}}>{t("subs.price.perMo")}</span>
                        </div>
                        {isFirst && <div style={{fontSize:10,color:"#94a3b8",fontWeight:500,marginTop:1,letterSpacing:.2}}>{t("subs.price.firstSite")}</div>}
                      </>
                    )}
                  </div>

                  {/* Action button */}
                  {isCanceling ? (
                    <button onClick={()=>{
                      const subLevelCancel = account?.cancel_at_period_end === true;
                      const otherFlagged = wsList.filter(w => !w.archived_at && w.cancel_at_period_end && w.id !== ws.id).length;

                      // Build the modal body based on context
                      let body;
                      if (subLevelCancel && otherFlagged > 0) {
                        body = `${t("subs.confirm.keepWs.subCancelOther.a")}${ws.name}${t("subs.confirm.keepWs.subCancelOther.b")}${ws.name}${t("subs.confirm.keepWs.subCancelOther.c")}${otherFlagged}${otherFlagged===1?t("subs.confirm.keepWs.subCancelOther.dWorkspace"):t("subs.confirm.keepWs.subCancelOther.dWorkspaces")}${t("subs.confirm.keepWs.subCancelOther.e")}${wsList.filter(w => !w.archived_at && w.cancel_at_period_end && w.id !== ws.id).map(w=>w.name).join(", ")}${t("subs.confirm.keepWs.subCancelOther.f")}${scheduledDate}${t("subs.confirm.keepWs.subCancelOther.g")}`;
                      } else if (subLevelCancel) {
                        body = `${t("subs.confirm.keepWs.subCancel.a")}${ws.name}${t("subs.confirm.keepWs.subCancel.b")}${ws.name}${t("subs.confirm.keepWs.subCancel.c")}`;
                      } else {
                        body = t("subs.confirm.keepWs.plain");
                      }

                      setConfirm({
                        title: `${t("subs.confirm.keepWs.titlePrefix")}${ws.name}${t("subs.confirm.keepWs.titleSuffix")}`,
                        body,
                        confirmLabel: `${t("subs.confirm.keepWs.confirmPrefix")}${ws.name}`,
                        confirmVariant: "primary",
                        onConfirm: async () => {
                          const session = await getCurrentSession();
                          const token = session?.access_token;
                          const res = await fetch(`${API_BASE}/api/stripe/uncancel-workspace`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                            body: JSON.stringify({ workspace_id: ws.id }),
                          });
                          const d = await res.json();
                          if (!res.ok) throw new Error(d.error || t("subs.err.failed"));
                          // Update local state: clear flag on THIS workspace only.
                          // Other flagged workspaces stay flagged. Account row updates via webhook + refresh.
                          setWorkspaces(p=>({...p, [ws.id]: {...p[ws.id], cancel_at_period_end: false, cancel_scheduled_for: null}}));
                        },
                      });
                    }} style={{padding:"7px 13px",borderRadius:7,border:"none",background:"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",color:"#fff",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",letterSpacing:"-.1px",whiteSpace:"nowrap",boxShadow:"0 2px 5px rgba(15,23,42,.16)"}}>{t("subs.btn.keep")}</button>
                  ) : (
                    <button onClick={()=>{
                      const activeCount = wsList.filter(w => !w.archived_at && !w.cancel_at_period_end).length;
                      const isLast = activeCount <= 1;
                      const hasActiveSubscription = !!account?.stripe_subscription_id;
                      const isTrialing = account?.subscription_status === "trialing" && !hasActiveSubscription;

                      // Trial user (no Stripe subscription yet): no billing involved, just delete the workspace.
                      // The Stripe-portal cancel flow doesn't apply because nothing's being billed.
                      if (isTrialing) {
                        setConfirm({
                          title: `${t("subs.confirm.deleteWs.titlePrefix")}${ws.name}${t("subs.confirm.deleteWs.titleSuffix")}`,
                          body: t("subs.confirm.deleteWs.body"),
                          confirmLabel: t("subs.confirm.deleteWs.confirmLabel"),
                          cancelLabel: t("subs.confirm.keepIt"),
                          confirmVariant: "danger",
                          onConfirm: async () => {
                            // Archive directly via Supabase. RLS allows users to update their own workspaces'
                            // archived_at field only via the protected trigger which we bypass via the backend.
                            // For trial deletions we use a backend endpoint that takes a service-role action.
                            try {
                              const session = await getCurrentSession();
                              const token = session?.access_token;
                              const res = await fetch(`${API_BASE}/api/stripe/cancel-workspace`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                                body: JSON.stringify({ workspace_id: ws.id, immediate: true }),
                              });
                              const d = await res.json();
                              if (!res.ok) throw new Error(d.error || t("subs.err.failedDeleteWorkspace"));
                              // Remove from local state
                              setWorkspaces(p => {
                                const next = {...p};
                                delete next[ws.id];
                                return next;
                              });
                            } catch (e) {
                              alert(t("subs.alert.couldNotDeletePrefix") + e.message);
                            }
                          },
                        });
                        return;
                      }

                      // Paid user with this being last workspace: route through Stripe Portal for full sub cancel
                      if (isLast) {
                        setConfirm({
                          title: t("subs.confirm.cancelSub.title"),
                          body: `${ws.name}${t("subs.confirm.cancelSub.bodyPrefix")}`,
                          confirmLabel: t("subs.confirm.cancelSub.confirmLabel"),
                          cancelLabel: t("subs.confirm.keepIt"),
                          confirmVariant: "danger",
                          onConfirm: async () => {
                            const session = await getCurrentSession();
                            const token = session?.access_token;
                            const res = await fetch(`${API_BASE}/api/stripe/create-portal`, {
                              method: "POST",
                              headers: { "Authorization": `Bearer ${token}` },
                            });
                            const d = await res.json();
                            if (d.url) window.location.href = d.url;
                            else throw new Error(d.error || t("subs.err.couldNotOpenPortal"));
                          },
                        });
                        return;
                      }
                      setConfirm({
                        title: `${t("subs.confirm.cancelWs.titlePrefix")}${ws.name}${t("subs.confirm.cancelWs.titleSuffix")}`,
                        body: t("subs.confirm.cancelWs.body"),
                        confirmLabel: t("subs.confirm.cancelWs.confirmLabel"),
                        cancelLabel: t("subs.confirm.keepIt"),
                        confirmVariant: "danger",
                        onConfirm: async () => {
                          const session = await getCurrentSession();
                          const token = session?.access_token;
                          const res = await fetch(`${API_BASE}/api/stripe/cancel-workspace`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                            body: JSON.stringify({ workspace_id: ws.id }),
                          });
                          const d = await res.json();
                          if (!res.ok) {
                            if (d.last_workspace) {
                              throw new Error(t("subs.err.lastWorkspaceManageBilling"));
                            }
                            throw new Error(d.error || t("subs.err.cancelFailed"));
                          }
                          setWorkspaces(p=>({...p, [ws.id]: {...p[ws.id], cancel_at_period_end: true, cancel_scheduled_for: d.archives_at}}));
                        },
                      });
                    }} style={{padding:"7px 13px",borderRadius:7,border:"1px solid #e2e8f0",background:"#fff",color:"#64748b",fontSize:12.5,fontWeight:500,cursor:"pointer",fontFamily:"inherit",letterSpacing:"-.1px"}}>{t("subs.btn.cancel")}</button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Archived workspaces — muted but polished */}
      {wsList.filter(w=>!!w.archived_at).length > 0 && (
        <>
          <div style={{display:"flex",alignItems:"center",gap:10,margin:"4px 0 12px"}}>
            <h2 style={{fontSize:12.5,fontWeight:700,color:"#475569",margin:0,letterSpacing:0.6,textTransform:"uppercase"}}>{t("subs.section.archived")}</h2>
            <div style={{flex:1,height:1,background:"#e2e8f0"}}/>
            <span style={{fontSize:11.5,color:"#94a3b8",fontWeight:500,letterSpacing:.2}}>{t("subs.archived.retentionNote")}</span>
          </div>
          <div style={{border:"1px solid #e2e8f0",borderRadius:10,overflow:"hidden",background:"#f8fafc",marginBottom:24,boxShadow:"0 1px 2px rgba(15,23,42,.02)"}}>
            {wsList.filter(w=>!!w.archived_at).map((ws, idx, arr)=>{
              const archivedDate = new Date(ws.archived_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
              const retentionEnd = new Date(new Date(ws.archived_at).getTime() + 90*86400000);
              const daysLeft = Math.max(0, Math.ceil((retentionEnd - new Date()) / 86400000));
              const isLast = idx === arr.length - 1;
              // A parked SCA row: created/restored but its prorated charge still
              // needs the bank's authentication. Not billed, no access; removed
              // automatically if the payment is never completed.
              const isPendingPayment = ws.status === "pending_payment";
              const doRestore = async () => {
                const session = await getCurrentSession();
                const token = session?.access_token;
                const res = await fetch(`${API_BASE}/api/stripe/restore-workspace`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                  body: JSON.stringify({ workspace_id: ws.id }),
                });
                const d = await res.json();
                // The prorated restore charge needs 3D-Secure / bank
                // authentication (402 + needs_checkout). Complete it on
                // Stripe Checkout; the webhook un-archives the workspace
                // once the payment lands.
                if (d.needs_checkout && d.checkout_url) {
                  window.location.href = d.checkout_url;
                  return;
                }
                // A payment session for this workspace is already open —
                // RESUME it (same session, no second charge).
                if (res.status === 409 && d.payment_url) {
                  window.location.href = d.payment_url;
                  return;
                }
                if ((res.status === 402 || d.code === "payment_required") && d.code !== "payment_failed") {
                  // Trial ended / no live subscription — send to the paywall
                  // instead of restoring for free. (A payment_failed 402 is a
                  // card decline on a LIVE subscription — show it inline below.)
                  setConfirm(null);
                  if (onShowUpgrade) onShowUpgrade();
                  else alert(t("subs.alert.trialEndedSubscribe"));
                  return;
                }
                if (!res.ok) throw new Error(d.error || t("subs.err.failed"));
                // Restore this workspace only. Other flagged workspaces keep their flags.
                setWorkspaces(p => ({
                  ...p,
                  [ws.id]: { ...p[ws.id], archived_at: null, cancel_at_period_end: false, cancel_scheduled_for: null, status: "active" }
                }));
              };
              return (
                <div key={ws.id} style={{padding:"13px 18px",borderBottom:isLast?"none":"1px solid #e2e8f0",display:"flex",alignItems:"center",gap:14}}>
                  <div style={{width:10,height:10,borderRadius:5,background:isPendingPayment?"#f59e0b":"#cbd5e1",flexShrink:0}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      <span style={{fontSize:13.5,fontWeight:500,color:"#64748b",letterSpacing:"-.1px"}}>{ws.name}</span>
                      {(ws.city||ws.area) && <span style={{fontSize:12.5,color:"#94a3b8"}}>{ws.city}{ws.area?`, ${ws.area}`:""}</span>}
                      {isPendingPayment
                        ? <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:"#fef3c7",color:"#92400e",fontWeight:700,letterSpacing:.4,textTransform:"uppercase"}}>Payment pending</span>
                        : <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:"#e2e8f0",color:"#475569",fontWeight:700,letterSpacing:.4,textTransform:"uppercase"}}>{t("subs.archived.pill")}</span>}
                    </div>
                    <div style={{fontSize:11.5,color:"#94a3b8",fontWeight:400,marginTop:3}}>
                      {isPendingPayment
                        ? "Waiting for payment authentication — complete it to add this workspace. It's removed automatically if the payment isn't completed."
                        : <>{t("subs.archived.metaPrefix")}{archivedDate} · {daysLeft}{daysLeft===1?t("subs.archived.metaDay"):t("subs.archived.metaDays")}{t("subs.archived.metaSuffix")}</>}
                    </div>
                  </div>
                  <button disabled={restoringWsId === ws.id} onClick={()=>{
                    if (isPendingPayment) {
                      // No confirm dialog — this resumes/settles the pending
                      // payment rather than starting a new charge. Guarded so a
                      // double-click can't fire two concurrent attempts.
                      if (restoringWsId) return;
                      setRestoringWsId(ws.id);
                      doRestore()
                        .catch(e => alert(e.message || t("subs.err.failed")))
                        .finally(() => setRestoringWsId(null));
                      return;
                    }
                    const subLevelCancel = account?.cancel_at_period_end === true;
                    const otherFlagged = wsList.filter(w => !w.archived_at && w.cancel_at_period_end).length;
                    let body;
                    if (subLevelCancel && otherFlagged > 0) {
                      body = `${t("subs.confirm.restore.subCancelOther.a")}${ws.name}${t("subs.confirm.restore.subCancelOther.b")}${ws.name}${t("subs.confirm.restore.subCancelOther.c")}${otherFlagged}${otherFlagged===1?t("subs.confirm.restore.subCancelOther.dWorkspace"):t("subs.confirm.restore.subCancelOther.dWorkspaces")}${t("subs.confirm.restore.subCancelOther.e")}`;
                    } else if (subLevelCancel) {
                      body = `${t("subs.confirm.restore.subCancel.a")}${ws.name}${t("subs.confirm.restore.subCancel.b")}`;
                    } else {
                      body = `${ws.name}${t("subs.confirm.restore.plain.a")}`;
                    }
                    setConfirm({
                      title: `${t("subs.confirm.restore.titlePrefix")}${ws.name}${t("subs.confirm.restore.titleSuffix")}`,
                      body,
                      confirmLabel: subLevelCancel ? t("subs.confirm.restore.confirmReactivate") : t("subs.confirm.restore.confirm"),
                      confirmVariant: "primary",
                      onConfirm: doRestore,
                    });
                  }} style={{padding:"7px 13px",borderRadius:7,border:"none",background:isPendingPayment?"linear-gradient(135deg, #b45309 0%, #92400e 100%)":"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",color:"#fff",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",letterSpacing:"-.1px",boxShadow:"0 2px 5px rgba(15,23,42,.18)"}}>{isPendingPayment ? "Complete payment" : t("subs.btn.restore")}</button>
                </div>
              );
            })}
          </div>
        </>
      )}



      {/* Add workspace form */}
      {!adding ? (
        wsList.length === 0 ? (
          <div style={{borderRadius:10,border:"1px dashed #cbd5e1",padding:32,textAlign:"center",marginBottom:16,background:"#fafbfc"}}>
            <div style={{fontSize:15,fontWeight:600,color:"#0f172a",marginBottom:6,letterSpacing:"-.2px"}}>{t("subs.empty.title")}</div>
            <div style={{fontSize:13,color:"#64748b",marginBottom:18,lineHeight:1.5,maxWidth:380,margin:"0 auto 18px"}}>{t("subs.empty.desc")}</div>
            <button onClick={()=>setAdding(true)} style={{padding:"9px 18px",borderRadius:7,border:"none",background:"#0f172a",color:"#fff",fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"inherit",letterSpacing:"-.1px"}}>{t("subs.addWorkspace")}</button>
          </div>
        ) : null
      ) : (
        <div style={{background:"#fff",borderRadius:14,border:`1px solid ${C.border}`,boxShadow:C.s3,overflow:"hidden",marginBottom:16}}>
          <div style={{background:`linear-gradient(135deg,#1a73e8 0%,#0d47a1 100%)`,padding:"20px 28px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:16,fontWeight:700,color:"#fff"}}>{t("subs.form.newWorkspace")}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,.7)",marginTop:2}}>{t("subs.form.trialNote")}</div>
            </div>
            <div style={{background:"rgba(255,255,255,.2)",borderRadius:20,padding:"4px 12px",fontSize:12,color:"#fff",fontWeight:500}}>{t("subs.form.stepPrefix")}{step}{t("subs.form.stepMid")}2</div>
          </div>
          <div style={{padding:28}}>
          {step===1&&(<>
            {isSuperAdmin ? (
              <div style={{marginBottom:24}}>
                <div style={{fontSize:12,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>{t("subs.form.selectMarket")}<span style={{textTransform:"none",color:C.blue,fontSize:11}}>{t("subs.form.superAdminOverride")}</span></div>
                <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  {Object.entries(MARKETS).map(([key,m])=>(
                    <button key={key} onClick={()=>switchMarket(key)}
                      style={{padding:"14px 16px",borderRadius:10,border:`2px solid ${market===key?C.blue:C.border}`,background:market===key?"#EBF2FF":"#fafafa",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                      <div style={{fontSize:18,marginBottom:4}}>{key==="uk"?"🇬🇧":key==="us"?"🇺🇸":key==="in"?"🇮🇳":"🇦🇪"}</div>
                      <div style={{fontSize:13,fontWeight:600,color:market===key?C.blue:C.text}}>{key==="uk"?t("subs.market.uk"):key==="us"?t("subs.market.us"):key==="in"?t("subs.market.in"):t("subs.market.uae")}</div>
                      <div style={{fontSize:11,color:C.muted,marginTop:3}}>{Object.keys(m.platforms).slice(0,3).join(" · ")}{Object.keys(m.platforms).length>3?"…":""}</div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{marginBottom:24,padding:"12px 14px",background:C.bg2,borderRadius:8,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:18}}>{market==="uae"?"🇦🇪":market==="us"?"🇺🇸":market==="in"?"🇮🇳":"🇬🇧"}</span>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:C.text}}>{market==="uae"?t("subs.market.uae"):market==="us"?t("subs.market.us"):market==="in"?t("subs.market.in"):t("subs.market.uk")}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>{t("subs.form.regionLocked.prefix")}{accountCurrency}</div>
                </div>
              </div>
            )}
            <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:6}}>{t("subs.form.label.brand")}</label>
                <input value={brand} onChange={e=>setBrand(e.target.value)} placeholder={t("subs.form.placeholder.brand")}
                  style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:14,color:C.text,background:"#fafafa",fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:6}}>{t("subs.form.label.city")}</label>
                <select value={city} onChange={e=>setCity(e.target.value)}
                  style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:14,color:city?C.text:"#999",background:"#fafafa",fontFamily:"inherit"}}>
                  <option value="">{t("subs.form.city.select")}</option>
                  {currentMarket.cities.map(c=><option key={c} value={c}>{c}</option>)}
                  <option value="other">{t("subs.form.city.other")}</option>
                </select>
                {city==="other"&&<input onChange={e=>setCity(e.target.value)} placeholder={t("subs.form.city.typeName")}
                  style={{width:"100%",marginTop:6,padding:"10px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:14,fontFamily:"inherit",boxSizing:"border-box"}}/>}
              </div>
            </div>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:11,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:6}}>{t("subs.form.label.area")}</label>
              <input value={area} onChange={e=>setArea(e.target.value)}
                placeholder={market==="uae"?t("subs.form.placeholder.area.uae"):market==="us"?t("subs.form.placeholder.area.us"):market==="in"?t("subs.form.placeholder.area.in"):t("subs.form.placeholder.area.uk")}
                style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:14,color:C.text,background:"#fafafa",fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            {brand&&city&&city!=="other"&&(
              <div style={{padding:"8px 14px",background:C.blue10,borderRadius:8,marginBottom:16,fontSize:13,color:C.blue}}>
                <strong>{brand} — {city}{area?` (${area})`:""}</strong>
              </div>
            )}
            {/* Aggregator selector — trial users pick 1, paid users get all */}
            {(() => {
              const isTrialing = !account || account.subscription_status === "trialing";
              const isFreeOverride = isOverrideActive(account);
              const restrictToOne = isTrialing && !isFreeOverride;

              if (restrictToOne) {
                // Trial: radio picker (single select)
                const platformEntries = Object.entries(currentPlatforms);
                const selectedPlatform = platforms[0] || platformEntries[0][0];
                return (
                  <div style={{padding:"16px 18px",background:"#fff",borderRadius:10,marginBottom:20,border:`1px solid #e2e8f0`,boxShadow:"0 1px 2px rgba(15,23,42,.04)"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,gap:12}}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{
                          fontSize:10,
                          fontWeight:700,
                          letterSpacing:.6,
                          textTransform:"uppercase",
                          color:"#475569",
                          background:"#f1f5f9",
                          padding:"3px 8px",
                          borderRadius:4,
                        }}>
                          {t("subs.form.freeTrial")}
                        </span>
                        <span style={{fontSize:13,fontWeight:600,color:"#0f172a",letterSpacing:"-.1px"}}>
                          {t("subs.form.selectAggregator")}
                        </span>
                      </div>
                    </div>
                    <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      {platformEntries.map(([p,def])=>{
                        const isPicked = selectedPlatform === p;
                        return (
                          <button
                            key={p}
                            type="button"
                            onClick={()=>setPlatforms([p])}
                            style={{
                              display:"flex",alignItems:"center",gap:10,
                              padding:"10px 12px",borderRadius:8,
                              border:`2px solid ${isPicked ? def.color : C.border}`,
                              background:isPicked ? def.color+"10" : "#fff",
                              cursor:"pointer",fontFamily:"inherit",textAlign:"left",
                              transition:"all .12s ease",
                            }}
                          >
                            <span style={{
                              width:16,height:16,borderRadius:"50%",
                              border:`2px solid ${isPicked ? def.color : "#cbd5e1"}`,
                              background:isPicked ? def.color : "#fff",
                              display:"flex",alignItems:"center",justifyContent:"center",
                              flexShrink:0,
                            }}>
                              {isPicked && <span style={{width:6,height:6,borderRadius:"50%",background:"#fff"}}/>}
                            </span>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:600,color:C.text}}>{p}</div>
                              <div style={{fontSize:11,color:C.muted}}>{t("subs.form.setRateInConfig")}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div style={{fontSize:11.5,color:"#64748b",marginTop:12,fontWeight:500,letterSpacing:"-.05px"}}>
                      {t("subs.form.trialOnePlatformNote")}
                    </div>
                  </div>
                );
              }

              // Paid / free-override: all platforms auto-included
              return (
                <div style={{padding:"12px 14px",background:C.bg2,borderRadius:8,marginBottom:20,border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:8}}>{market==="uae"?"🇦🇪":market==="us"?"🇺🇸":market==="in"?"🇮🇳":"🇬🇧"}{t("subs.form.allPlatformsIncluded")}</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {Object.entries(currentPlatforms).map(([p,def])=>(
                      <span key={p} style={{padding:"4px 10px",borderRadius:6,fontSize:11,fontWeight:600,background:def.color+"18",color:def.color,border:`1px solid ${def.color}44`}}>
                        ✓ {p}
                      </span>
                    ))}
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginTop:8}}>{t("subs.form.setRatesInConfig")}</div>
                </div>
              );
            })()}
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>{if(brand&&city)setStep(2);}}
                style={{padding:"11px 28px",borderRadius:8,border:"none",background:C.blue,color:"#fff",cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit",boxShadow:C.s2}}>
                {t("subs.form.continue")}
              </button>
              {!showAddOnly&&<button onClick={()=>{setAdding(false);setStep(1);}}
                style={{padding:"11px 20px",borderRadius:8,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",fontSize:14,fontFamily:"inherit"}}>
                {t("subs.form.cancel")}
              </button>}
            </div>
          </>)}
          {step===2&&(()=>{
            // Determine payment state — workspace is free during trial; Stripe only after trial expires
            const hasActiveSubscription = account && account.stripe_subscription_id && (account.subscription_status === "active" || account.subscription_status === "trialing");
            const isSuperAdmin = user?.plan === "superadmin";
            const isFreeOverride = isOverrideActive(account);

            // Trial state. CRITICAL: a trial is ONE-TIME. We key "pre-trial"
            // off the durable `trial_used` flag, NOT trial_ends_at — because
            // trial_ends_at gets nulled on conversion and can be null for a
            // cancelled-trial account, which previously made the app wrongly
            // offer a second free trial ("retrial"). trial_used is set true the
            // first time a trial starts and never cleared.
            const now = new Date();
            const trialEnd = account?.trial_ends_at ? new Date(account.trial_ends_at) : null;
            const hasUsedTrial = account?.trial_used === true;
            const isPreTrial = !hasUsedTrial && !account?.trial_ends_at;
            const isInActiveTrial = account?.subscription_status === "trialing" && trialEnd && trialEnd > now;
            // Free access during trial only if: never used a trial (genuine
            // pre-trial) OR currently inside an unexpired trial. A used/expired/
            // cancelled trial does NOT allow free — it must go through payment.
            const trialAllowsFree = isPreTrial || isInActiveTrial;

            // Stripe only kicks in if: not super admin, not free override, no existing subscription, AND trial has expired
            const needsPayment = !isSuperAdmin && !isFreeOverride && !hasActiveSubscription && !trialAllowsFree;

            // Calculate pricing for display.
            // "First site" rate (250) applies to the cheapest billable workspace; "additional"
            // rate (195) applies to every workspace beyond that. So whether the NEW workspace
            // we're about to create is treated as first vs additional depends on how many
            // BILLABLE workspaces already exist — archived or cancel-pending ones don't count,
            // because the backend's syncSubscriptionItems re-tiers automatically when a billable
            // workspace drops off (countBillableWorkspaces excludes archived + cancel-pending).
            const wsCurrency = market === "uae" ? "AED" : market === "us" ? "USD" : market === "in" ? "INR" : "GBP";
            const symbol = getCurrencySymbol(wsCurrency);
            const billableCount = Object.values(workspaces || {}).filter(w => !w.archived_at && !w.cancel_at_period_end).length;
            const newSiteIsFirst = billableCount === 0;
            const monthlyRate = newSiteIsFirst ? getFirstSiteRate(wsCurrency) : getAdditionalRate(wsCurrency);

            return (<>
              <div style={{fontSize:15,fontWeight:600,color:C.text,marginBottom:20}}>{t("subs.confirmStep.title")}</div>
              <div style={{background:C.bg2,borderRadius:10,padding:20,marginBottom:20}}>
                {[[t("subs.confirmStep.row.market"),market==="uae"?t("subs.market.label.uae"):market==="us"?t("subs.market.label.us"):market==="in"?t("subs.market.label.in"):t("subs.market.label.uk")],[t("subs.confirmStep.row.workspace"),`${brand} — ${city}${area?` (${area})`:""}`],[t("subs.confirmStep.row.platforms"),platforms.join(", ")],[t("subs.confirmStep.row.currency"),`${currentMarket.sym.trim()} (${currentMarket.currency})`]].map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",paddingBottom:10,marginBottom:10,borderBottom:`1px solid ${C.border2}`,fontSize:14}}>
                    <span style={{color:C.muted,fontWeight:500}}>{l}</span>
                    <span style={{fontWeight:600,color:C.text}}>{v}</span>
                  </div>
                ))}
                <div style={{display:"flex",justifyContent:"space-between",fontSize:14}}>
                  <span style={{color:C.muted,fontWeight:500}}>{needsPayment ? t("subs.confirmStep.price") : t("subs.confirmStep.cost")}</span>
                  <span style={{fontWeight:700,color:needsPayment ? C.text : C.green,fontSize:15}}>
                    {isFreeOverride || isSuperAdmin
                      ? t("subs.confirmStep.freeOverride")
                      : needsPayment
                        ? `${symbol}${monthlyRate.toFixed(monthlyRate < 1 ? 3 : 2)}${t("subs.confirmStep.perMo")}`
                        : trialAllowsFree && !hasActiveSubscription
                          ? `${t("subs.confirmStep.freeDuringTrialPrefix")}${symbol}${monthlyRate.toFixed(monthlyRate < 1 ? 3 : 2)}${t("subs.confirmStep.perMoAfter")}`
                          : hasActiveSubscription && !newSiteIsFirst
                            ? `+${symbol}${monthlyRate.toFixed(monthlyRate < 1 ? 3 : 2)}${t("subs.confirmStep.addedToSubSuffix")}`
                            : t("subs.confirmStep.included")}
                  </span>
                </div>
              </div>
              {needsPayment ? (
                <div style={{padding:"12px 16px",background:"#EBF2FF",borderRadius:8,marginBottom:20,fontSize:13,color:C.blue,display:"flex",alignItems:"flex-start",gap:10}}>
                  <span style={{fontSize:16,lineHeight:1}}>💳</span>
                  <div>
                    <div style={{fontWeight:600,marginBottom:3}}>{t("subs.confirmStep.subRequired")}</div>
                    <div style={{color:C.muted,fontSize:12,lineHeight:1.5}}>{t("subs.confirmStep.subRequired.desc")}</div>
                  </div>
                </div>
              ) : trialAllowsFree && !hasActiveSubscription ? (
                <div style={{padding:"12px 16px",background:"#E8F5E9",borderRadius:8,marginBottom:20,fontSize:13,color:"#2E7D32",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:16}}>✓</span>
                  <span>{t("subs.confirmStep.freeDuringTrialBanner")}</span>
                </div>
              ) : hasActiveSubscription && !newSiteIsFirst ? (
                <div style={{padding:"12px 16px",background:"#E8F5E9",borderRadius:8,marginBottom:20,fontSize:13,color:"#2E7D32",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:16}}>✓</span>
                  <span>{t("subs.confirmStep.addedToSubBanner.prefix")}{symbol}{monthlyRate.toFixed(2)}{t("subs.confirmStep.addedToSubBanner.suffix")}</span>
                </div>
              ) : (
                <div style={{padding:"12px 16px",background:"#E8F5E9",borderRadius:8,marginBottom:20,fontSize:13,color:"#2E7D32",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:16}}>✓</span>
                  <span>{isSuperAdmin ? t("subs.confirmStep.superAdminNoCharge") : t("subs.confirmStep.includedOnPlan")}</span>
                </div>
              )}
              <div style={{display:"flex",gap:10}}>
                <button onClick={async ()=>{
                  // Wrap the workspace creation in a confirm dialog whenever it triggers a
                  // real billing change. Two cases:
                  //   (a) Adding an ADDITIONAL workspace to an existing active subscription
                  //       → charged at the "additional" rate (195 AED for UAE).
                  //   (b) Adding what becomes the FIRST billable workspace on a subscription
                  //       that's still active but has zero billable workspaces (e.g. user
                  //       cancelled their only workspace and is now restarting)
                  //       → charged at the "first" rate (250 AED for UAE).
                  // For trial / free-override / super-admin flows, no confirmation needed
                  // because there's no immediate billing impact.
                  const isActiveSub = hasActiveSubscription
                    && account?.subscription_status === "active";   // exclude trialing
                  const willChargeProration = isActiveSub
                    && !isSuperAdmin
                    && !isFreeOverride;

                  const doCreate = async () => {
                    setSaving(true);
                    try {
                      // ATOMIC PATH (active subscription + billing change):
                      // For workspace additions to an active subscription, use
                      // the server-side transactional endpoint so workspace
                      // creation + Stripe sync are a single request — no
                      // tab-close race where the workspace exists in DB but
                      // Stripe never got told.
                      //
                      // LEGACY PATH (trial, free override, super admin, first signup):
                      // Use the existing createWorkspace + sync-quantity flow.
                      // No race risk because no immediate Stripe charge is in
                      // play.
                      const useAtomicPath = willChargeProration && !needsPayment;

                      if (useAtomicPath) {
                        // platformList: derive from state, matching createWorkspace's logic.
                        // For paid users we use all selected platforms (or all defaults if
                        // none specifically selected).
                        const platformList = platforms.length > 0 ? platforms : Object.keys(currentPlatforms);

                        // Build the channels payload from current form selections.
                        const channels = platformList.map(p => {
                          const cfg = currentPlatforms[p] || {};
                          return {
                            platform:     p,
                            color:        cfg.color || "#1a73e8",
                            commission:   cfg.commission ?? 0,
                            card_fee_pct: 0,
                          };
                        });

                        const session = await getCurrentSession();
                        const token = session?.access_token;
                        const res = await fetch(`${API_BASE}/api/workspaces/create-billed`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                          body: JSON.stringify({
                            name:     brand,
                            market,
                            city,
                            area:     area || undefined,
                            color:    channels[0]?.color || "#1a73e8",
                            channels,
                          }),
                        });
                        const data = await res.json();
                        // The prorated charge needs 3D-Secure / bank authentication
                        // (common for UK/EU cards and Link) — arrives as a 402 with
                        // needs_checkout. The workspace is parked server-side;
                        // complete the payment on Stripe Checkout and the webhook
                        // activates it on return.
                        if (data.needs_checkout && data.checkout_url) {
                          window.location.href = data.checkout_url;
                          return;
                        }
                        if (!res.ok) {
                          if (res.status === 402) {
                            alert(data.error || t("subs.alert.cardCouldNotCharge"));
                          } else {
                            alert(data.error || t("subs.alert.createFailed"));
                          }
                          setSaving(false);
                          return;
                        }
                        // Success — server has both the workspace AND the
                        // Stripe charge committed. Update local state.
                        const ws = data.workspace;
                        const newChannelsMap = {};
                        (ws.channels || []).forEach(ch => {
                          newChannelsMap[ch.id] = {
                            id: ch.id, platform: ch.platform, color: ch.color,
                            commission: ch.commission, history: [], menu: [],
                          };
                        });
                        setWorkspaces(prev => ({
                          ...prev,
                          [ws.id]: {
                            id: ws.id, name: ws.name, city: ws.city, area: ws.area,
                            market: ws.market, currency: ws.currency,
                            color: ws.color, platform: platformList.join(" + "),
                            status: ws.status, history: [], menu: [],
                            fixedCosts: { rent: 0, wages: 0, utilities: 0, other: 0 },
                            channels: newChannelsMap,
                          },
                        }));
                        setBrand(""); setCity(""); setArea("");
                        setAdding(false); setStep(1);
                        if (refreshAccount) setTimeout(() => refreshAccount(), 500);
                        setSaving(false);
                        return;
                      }

                      // LEGACY (non-atomic) path: trial, free-override, super-admin, first signup.
                      const newWsId = await createWorkspace();
                      if (!newWsId && !needsPayment) {
                        setSaving(false);
                        return;
                      }
                      if (needsPayment) {
                        const session = await getCurrentSession();
                        const token = session?.access_token;
                        const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                          body: JSON.stringify({ plan: "monthly" }),
                        });
                        const data = await res.json();
                        if (data.url) window.location.href = data.url;
                        else { alert(data.error || t("subs.err.checkoutFailed")); setSaving(false); }
                      } else {
                        setSaving(false);
                      }
                    } catch (e) {
                      console.error(e);
                      alert(t("subs.alert.createFailedRetry"));
                      setSaving(false);
                    }
                  };

                  if (willChargeProration) {
                    setConfirm({
                      title: `${t("subs.confirmStep.confirm.addPrefix")}${brand}${t("subs.confirmStep.confirm.addSuffix")}`,
                      body: (
                        <div>
                          <div style={{fontSize:13.5,color:"#475569",lineHeight:1.6,marginBottom:14}}>
                            {t("subs.confirmStep.confirm.intro")}
                          </div>
                          <ul style={{margin:0,padding:"0 0 12px 18px",fontSize:13,color:"#0f172a",lineHeight:1.7}}>
                            <li>{t("subs.confirmStep.confirm.bullet1.prefix")}<b>{symbol}{monthlyRate.toFixed(monthlyRate < 1 ? 3 : 2)}{t("subs.confirmStep.confirm.bullet1.suffix")}</b></li>
                            <li>{t("subs.confirmStep.confirm.bullet2")}</li>
                            <li><b>{brand}</b>{t("subs.confirmStep.confirm.bullet3.suffix")}</li>
                          </ul>
                          <div style={{padding:"10px 12px",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:7,fontSize:12,color:"#64748b",lineHeight:1.5}}>
                            {t("subs.confirmStep.confirm.modifyNote")}
                          </div>
                        </div>
                      ),
                      confirmLabel: `${t("subs.confirmStep.confirm.label.addPrefix")}${brand} · ${symbol}${monthlyRate.toFixed(monthlyRate < 1 ? 3 : 2)}${t("subs.confirmStep.perMo")}`,
                      confirmVariant: "primary",
                      onConfirm: doCreate,
                    });
                  } else {
                    // No prorated charge — proceed directly. Trial, free, or super-admin path.
                    doCreate();
                  }
                }} disabled={saving}
                  style={{padding:"12px 28px",borderRadius:8,border:"none",background:saving?C.muted:(needsPayment?C.blue:C.green),color:"#fff",cursor:saving?"not-allowed":"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit",boxShadow:C.s2}}>
                  {saving ? t("subs.confirmStep.btn.processing") : needsPayment ? t("subs.confirmStep.btn.continuePayment") : (hasActiveSubscription && !newSiteIsFirst ? t("subs.confirmStep.btn.addSite") : t("subs.confirmStep.btn.createWorkspace"))}
                </button>
                <button onClick={()=>setStep(1)}
                  style={{padding:"12px 20px",borderRadius:8,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",fontSize:14,fontFamily:"inherit"}}>
                  {t("subs.confirmStep.btn.back")}
                </button>
              </div>
            </>);
          })()}
          </div>
        </div>
      )}
      {/* Full-screen loading overlay while a workspace is being created
          (trial createWorkspace() or paid /api/workspaces/create-billed). Matches
          the UpgradeModal backdrop pattern; sits above it (higher zIndex). */}
      {saving && (
        <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",zIndex:1100,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:18,padding:20}}>
          <div style={{width:42,height:42,border:`3px solid rgba(255,255,255,0.35)`,borderTopColor:"#fff",borderRadius:"50%",animation:"subsCreateSpin 0.8s linear infinite"}}/>
          <div style={{color:"#fff",fontSize:15,fontWeight:600,letterSpacing:"-.2px"}}>{t("subs.overlay.creatingWorkspace")}</div>
          <style>{`@keyframes subsCreateSpin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}
      {confirm && <ConfirmDialog {...confirm} onClose={()=>setConfirm(null)}/>}
    </div>
  );
}
